import React from 'react'
import StdButton from '../StdButton/StdButton'
import Add from '../Icons/Add/Add'
import PlayButton from '../Icons/PlayButton/PlayButton'
import PropTypes from 'prop-types'
import Checkmark from '../Icons/Checkmark/Checkmark'
import { connect } from 'react-redux'
import focusable from '../Focusable/Focusable'
import {
  getFavorites,
  getAllFavorites,
  addToFavorites,
  removeFromFavorites,
} from '../../actions/contentActions'
import { triggerSentryError } from '../../../../shared/helpers.js'
import { hasNoNullishValues } from '../../lib/helpers.ts'
import tts from '../../lib/TTS'
import * as UI from '../../lib/ui'
const START_WATCHING = 'Start Watching'
const RESUME = 'Resume'

export const MY_LIST_BUTTON_TYPE = 'myListButtonType'
export const PLAY_BUTTON_TYPE = 'playButtonType'

export class ActionButtons extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      id: 'ActionButtons',
      isFavorited: false,
    }
    this.myListRef = React.createRef()
    this.restartRef = React.createRef()
    this.resumeRef = React.createRef()
  }

  componentDidMount() {
    UI.registerFocusableComponent({ screen: this.state.id })
    this.checkFavorites(this.props.showDetail.pageDetails.cid)
  }

  onFocus() {
    setTimeout(() => {
      this.resumeRef.current
        ? this.resumeRef.current.setFocus()
        : this.myListRef.current.setFocus()
    }, 100)
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.favorites !== this.props.favorites &&
      Array.isArray(this.props.favorites)
    ) {
      this.checkFavorites(this.props.showDetail.pageDetails.cid)
    }
  }

  isFavorited(favorites, showId) {
    return !!favorites?.find((y) => y.cid === showId)
  }

  checkFavorites(showId) {
    const { favorites, favoritesFullyLoaded, UID } = this.props
    if (favorites === null) {
      getFavorites(UID)
    } else if (showId === null || favorites === 'error') {
      return
    } else {
      const isFavorited = this.isFavorited(favorites, showId)
      if (isFavorited || favoritesFullyLoaded) {
        this.setState({
          isFavorited: isFavorited,
        })
      } else {
        getAllFavorites(UID)
      }
    }
  }

  addToFavorites = async () => {
    const { pageDetails } = this.props.showDetail
    const { UID } = this.props

    addToFavorites(UID, pageDetails)
      .then(() => {
        tts.addTextToRead('Added to My List')
        this.setState({
          isFavorited: true,
        })
      })
      .catch((err) => {
        console.error(err)
        this.props.toggleError({
          title: 'There was an error adding this show to your Favorites.',
        })

        const errorDescription = `Error ${
          err.status ? err.status : '(unknown)'
        } adding show ${pageDetails.title} to favorites`

        triggerSentryError('addToFavorites', 'ShowDetailsScreen.js', {
          errorMessage: err.message ?? 'Undefined error message (default)',
          errorDescription,
          errorLocation: 'ShowDetailsScreen - addToFavorites',
        })
      })
  }

  removeFromFavorites = async () => {
    const { pageDetails } = this.props.showDetail
    const { UID } = this.props
    removeFromFavorites(UID, pageDetails.cid)
      .then(() => {
        tts.addTextToRead('Removed from My List')
        this.setState({
          isFavorited: false,
        })
      })
      .catch((err) => {
        console.error(err)
        this.props.toggleError({
          title: 'There was an error removing this show from your Favorites.',
        })

        const errorDescription = `Error ${
          err.status ? err.status : '(unknown)'
        } removing show ${pageDetails.title} from favorites`

        triggerSentryError(
          'removeFromFavorites',
          'ShowDetailsScreen.js',
          {
            errorMessage: err.message ?? 'Undefined error message (default)',
            errorDescription,
            errorLocation: 'ShowDetailsScreen - removeFromFavorites',
          },
          true
        )
      })
  }

  toggleFavorites = async () => {
    if (!this.state.isFavorited) {
      this.addToFavorites()
    } else {
      this.removeFromFavorites()
    }
  }

  onKeyEvent(key) {
    let handled = false
    if (
      key === 'UP' &&
      this.resumeRef.current?.isFocused() &&
      this.myListRef.current
    ) {
      this.myListRef.current.setFocus()
      handled = true
    }

    if (
      key === 'DOWN' &&
      this.myListRef.current?.isFocused() &&
      this.resumeRef.current
    ) {
      this.resumeRef.current.setFocus()
      handled = true
    }

    return handled
  }

  onMyListFocus = () => {
    tts.stopRead()
    tts.addTextToRead(
      `Button, ${this.state.isFavorited ? 'Remove from ' : 'Add to '} My List`
    )
  }

  onResumeFocus = () => {
    tts.stopRead()
    const { contentHasSecondsWatched, contentSeason, contentEpisode } =
      this.props.showDetail.continueWatchingData

    tts.addTextToRead(
      `Button, ${
        contentHasSecondsWatched ? RESUME : START_WATCHING
      } Season ${contentSeason} Episode ${contentEpisode}`
    )
  }

  renderMyListButton() {
    const { isFavorited } = this.state
    return (
      <StdButton
        ref={this.myListRef}
        parent={this}
        componentType={MY_LIST_BUTTON_TYPE}
        link={false}
        onFocus={this.onMyListFocus}
        preventReadTextOnFocus={true}
        label="My List"
        onClick={this.toggleFavorites}
      >
        {!isFavorited && <Add height="20px" fill={'#fff'} />}
        {isFavorited && <Checkmark height="20px" fill={'#fff'} />}
        <span className="action-label">My List</span>
      </StdButton>
    )
  }

  renderResumeButton() {
    const {
      contentHasSecondsWatched,
      contentSeason,
      contentEpisode,
      contentResourceType,
      contentUrl,
    } = this.props.showDetail.continueWatchingData

    //Todo: Should we put content, or special, or title when we dont have season and episode?
    //Todo: This bug was found when landing on the nova show details.
    const season = contentSeason ? contentSeason : ''
    const episode = contentEpisode ? contentEpisode : ''

    const buttonLabel = `${
      contentHasSecondsWatched ? RESUME : START_WATCHING
    } ${
      season && episode
        ? `S${season} E${episode}`
        : `${contentResourceType
            .charAt(0)
            .toUpperCase()}${contentResourceType.slice(1)}`
    }`
    return (
      <StdButton
        id="resume"
        ref={this.resumeRef}
        parent={this}
        componentType={PLAY_BUTTON_TYPE}
        onFocus={this.onResumeFocus}
        preventReadTextOnFocus={true}
        label={`${
          contentHasSecondsWatched ? RESUME : START_WATCHING
        } S${contentSeason} E${contentEpisode}`}
        to={`/video-details/${encodeURIComponent(contentUrl)}`}
        link={true}
      >
        <PlayButton height="21px" width="16px" fill={'#fff'} />
        <span className="action-label">{buttonLabel}</span>
      </StdButton>
    )
  }

  render() {
    const isContinueWatchingDataAvailable = hasNoNullishValues(
      this.props.showDetail.continueWatchingData
    )

    return (
      <>
        {this.renderMyListButton()}
        {isContinueWatchingDataAvailable && this.renderResumeButton()}
      </>
    )
  }
}

ActionButtons.propTypes = {
  isFavorited: PropTypes.bool,
  toggleError: PropTypes.func,
  showDetail: PropTypes.shape({
    pageDetails: PropTypes.shape({
      title: PropTypes.string,
      logo: PropTypes.string,
      mezzanineImage: PropTypes.string,
      cid: PropTypes.string,
    }),
    continueWatchingData: PropTypes.shape({
      contentHasSecondsWatched: PropTypes.bool,
      contentTitle: PropTypes.string,
      contentSeason: PropTypes.number,
      contentEpisode: PropTypes.number,
      contentResourceType: PropTypes.string,
      contentUrl: PropTypes.string,
    }),
  }),
  favorites: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  favoritesFullyLoaded: PropTypes.bool,
  UID: PropTypes.string,
}

const mapStateToProps = (state) => {
  return {
    showDetail: state.showDetail,
    UID: state.device.UID,
    favorites: state.personalization.favorites,
    favoritesFullyLoaded: state.personalization.favoritesFullyLoaded,
  }
}

export default connect(mapStateToProps, null, null, { forwardRef: true })(
  focusable(ActionButtons)
)
